import React from "react";

/**
 * The opening of each interior page: an oversized type block, then the
 * photograph as a full-bleed letterbox band.
 *
 * Third design, and the reasoning for landing here is worth keeping.
 *
 * First attempt was text over a darkened photograph. That needs a heavy scrim to
 * stay readable, so every page opened on the same dark ground whatever the
 * picture was, and the scrim ruined the picture it was covering.
 *
 * Second was a type panel beside the image in a 7/5 split — which fixed the
 * contrast but is its own period piece: the split hero has been the default
 * agency layout since about 2014, and a box next to a box reads as a template.
 *
 * Third was type above a full-bleed letterbox band — better, but a band is
 * still a rectangle in a slot, and "image above text" is its own tired shape.
 *
 * So the picture stops being an object on the page. It is masked to nothing
 * across the side the type occupies and only resolves on the far side, with its
 * top and bottom edges fading back into the panel colour. There is no boundary
 * where photograph becomes background, and no rectangle anywhere: type and image
 * share one field.
 *
 * This also settles the contrast question for good. Where the words are, the
 * mask has taken the image to fully transparent, so the type sits on flat colour
 * — no scrim, no compromise, and the photograph is never darkened.
 *
 * `tone` and `flip` keep the pages from marching in step: each picks its ground
 * from the palette and which side its picture resolves on.
 */
const TONES = {
  ink: {
    hex: "#1C2722",
    panel: "bg-[#1C2722]",
    heading: "text-[#F9F8F6]",
    body: "text-[#D1CEC7]",
    eyebrow: "!text-[#D1CEC7]",
    hair: "bg-[#F9F8F6]/20",
  },
  stone: {
    hex: "#F2EFEA",
    panel: "bg-[#F2EFEA]",
    heading: "text-[#1C2722]",
    body: "text-[#4A5A52]",
    eyebrow: "",
    hair: "bg-[#1C2722]/15",
  },
  bone: {
    hex: "#F9F8F6",
    panel: "bg-[#F9F8F6]",
    heading: "text-[#1C2722]",
    body: "text-[#4A5A52]",
    eyebrow: "",
    hair: "bg-[#1C2722]/15",
  },
  cream: {
    hex: "#EFEBE2",
    panel: "bg-[#EFEBE2]",
    heading: "text-[#1C2722]",
    body: "text-[#4A5A52]",
    eyebrow: "",
    hair: "bg-[#1C2722]/15",
  },
};

export const PageMasthead = ({
  image,
  imageSrcSet,
  imageAlt = "",
  tone = "stone",
  flip = false,          // resolve the picture on the left instead of the right
  eyebrow,
  title,
  lede,
  children,
  testId,
}) => {
  const t = TONES[tone] || TONES.stone;

  return (
    <section
      className={`relative isolate overflow-hidden border-b hairline ${t.panel}`}
      style={{ "--panel": t.hex }}
      data-testid={testId}
      data-tone={tone}
    >
      {/* The photograph, dissolving. `scaleX(-1)` rather than a second mask when
          flipped: mirroring the image is invisible on a landscape like these and
          saves maintaining two gradients that must stay in sync. */}
      <div
        className="absolute inset-0 -z-10 image-dissolve"
        style={flip ? { transform: "scaleX(-1)" } : undefined}
      >
        <img
          src={image}
          srcSet={imageSrcSet}
          sizes="100vw"
          alt={imageAlt}
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover grayscale-[20%] contrast-[0.98]"
        />
      </div>
      {/* Top and bottom edges eased back into the panel colour. */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 panel-fade-y" />

      <div
        className={`relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-28 ${
          flip ? "lg:flex lg:flex-col lg:items-end lg:text-right" : ""
        }`}
      >
        {/* Held to half the width, and the mask stays fully transparent to 52%.
            The two numbers are a pair: the type must end before the picture
            starts to resolve, or the headline ends up over photograph and the
            whole point of the dissolve is lost. */}
        <div className="lg:max-w-[50%]">
          {eyebrow && (
            <div className={`flex items-center gap-5 mb-7 ${flip ? "lg:flex-row-reverse" : ""}`}>
              <span className={`overline shrink-0 ${t.eyebrow}`}>{eyebrow}</span>
              <span aria-hidden="true" className={`h-px flex-1 ${t.hair}`} />
            </div>
          )}

          <h1
            className={`font-serif text-[clamp(2.2rem,4.8vw,3.9rem)] leading-[0.98] tracking-[-0.02em] ${t.heading}`}
          >
            {title}
          </h1>

          {lede && (
            <p className={`mt-8 text-[16px] leading-relaxed max-w-xl ${t.body} ${flip ? "lg:ml-auto" : ""}`}>
              {lede}
            </p>
          )}

          {children}
        </div>
      </div>
    </section>
  );
};

export default PageMasthead;
